import { Button, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDate } from "@/lib/utils/date";

type Recap =
  paths["/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/consignes-recap"]["get"]["responses"][200]["content"]["application/json"]["data"];
type RecapBase = Recap["bases"][number];
type RecapItem = RecapBase["items"][number];

export type ConsignesRecapTarget = {
  documentId: number;
  versionId: number;
  /** « N° - intitulé » */
  documentName: string;
  versionIndex: string | null;
};

export type ConsignesRecapDialogProps = {
  yardId: string;
  target: ConsignesRecapTarget | null;
  onClose: () => void;
  /** Opens the visa EG or exam report PDF. */
  onOpenFile: (fichierId: number) => void;
};

const itemKey = (item: RecapItem) => `${item.type}:${item.id}`;

/**
 * Check-list of a document version (N8): consignes passed after exam report
 * observations and visa EG remarks, with their base. The pilot ticks what the
 * next version took into account; the rest carries over to it.
 */
export function ConsignesRecapDialog({
  yardId,
  target,
  onClose,
  onOpenFile,
}: ConsignesRecapDialogProps) {
  const [recap, setRecap] = useState<Recap | "loading" | "error">("loading");
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const documentId = target?.documentId;
  const versionId = target?.versionId;
  useEffect(() => {
    if (isNil(documentId) || isNil(versionId)) return;
    let cancelled = false;
    setRecap("loading");
    apiClient
      .GET(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/consignes-recap",
        { params: { path: { yardId, documentId, versionId } } },
      )
      .then(({ data }) => {
        if (!cancelled) setRecap(data?.data ?? "error");
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, documentId, versionId]);

  const toggle = async (item: RecapItem, prisEnCompte: boolean) => {
    if (isNil(documentId) || isNil(versionId)) return;
    setSavingKey(itemKey(item));
    try {
      const { error } = await apiClient.PUT(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/consignes-recap/prise-en-compte",
        {
          params: { path: { yardId, documentId, versionId } },
          body: { type: item.type, id: item.id, prisEnCompte },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Erreur",
          description: "Impossible d'enregistrer la prise en compte.",
        });
        return;
      }
      setRecap((current) =>
        typeof current === "string"
          ? current
          : {
              ...current,
              bases: current.bases.map((base) => ({
                ...base,
                items: base.items.map((other) =>
                  itemKey(other) === itemKey(item)
                    ? { ...other, prisEnCompte }
                    : other,
                ),
              })),
            },
      );
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Modal.Root
      open={isNotNil(target)}
      onOpenChange={(e) => !e.open && onClose()}
    >
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="2xl">
          <Modal.Header>
            <Modal.Title>Récap des consignes</Modal.Title>
          </Modal.Header>
          <div className="space-y-4 px-6 pb-6">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              {`${target?.documentName ?? ""} - Indice ${target?.versionIndex ?? "-"}`}
            </p>
            {recap === "loading" && (
              <p className="text-sm text-zinc-500">Chargement...</p>
            )}
            {recap === "error" && (
              <p className="text-sm text-red-600">
                Erreur lors du chargement du récap.
              </p>
            )}
            {typeof recap !== "string" && recap.bases.length === 0 && (
              <p className="text-sm text-zinc-500">
                Aucune consigne ni remarque de visa EG sur cet indice.
              </p>
            )}
            {typeof recap !== "string" && recap.bases.length > 0 && (
              <>
                {recap.canCheck && (
                  <p className="text-xs text-zinc-500">
                    Cochez ce que la version suivante a pris en compte : les
                    points non cochés passent sur l'indice suivant.
                  </p>
                )}
                <div className="grid grid-cols-[1fr_16rem] gap-x-6 text-xs font-medium uppercase tracking-wide text-zinc-500">
                  <span>Actions demandées</span>
                  <span>Base</span>
                </div>
                {recap.bases.map((base) => (
                  <section
                    key={`${base.type}:${base.id}`}
                    className="grid grid-cols-[1fr_16rem] gap-x-6 border-t border-zinc-200 pt-3 dark:border-zinc-700"
                  >
                    <ul className="space-y-2">
                      {base.items.map((item) => (
                        <li key={itemKey(item)}>
                          <label className="flex items-start gap-2 text-sm text-zinc-800 dark:text-zinc-200">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={item.prisEnCompte}
                              disabled={
                                !recap.canCheck || savingKey === itemKey(item)
                              }
                              onChange={(event) =>
                                void toggle(item, event.target.checked)
                              }
                            />
                            <span className="min-w-0 flex-1">
                              <span className="whitespace-pre-line">
                                {item.description}
                              </span>
                              {item.sourceVersionId !== versionId && (
                                <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                  {`Indice ${item.sourceIndex ?? "-"}`}
                                </span>
                              )}
                              {isNotNil(item.observation) && (
                                <span className="mt-0.5 block text-xs text-zinc-500">
                                  {`Observation : ${item.observation}`}
                                </span>
                              )}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <BaseCard base={base} onOpenFile={onOpenFile} />
                  </section>
                ))}
              </>
            )}
          </div>
          <Modal.Footer>
            <Button onClick={onClose}>Fermer</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}

function BaseCard({
  base,
  onOpenFile,
}: {
  base: RecapBase;
  onOpenFile: (fichierId: number) => void;
}) {
  const title =
    base.type === "visa"
      ? `Visa EG n° ${base.numero ?? "-"}`
      : `Rapport d'examen n° ${base.numero ?? "-"}`;
  const { fichierId } = base;
  return (
    <div className="space-y-0.5 text-sm">
      <p className="font-medium text-zinc-800 dark:text-zinc-200">{title}</p>
      {isNotNil(base.companyName) && (
        <p className="text-zinc-600 dark:text-zinc-400">{base.companyName}</p>
      )}
      {isNotNil(base.date) && (
        <p className="text-zinc-600 dark:text-zinc-400">
          {`Transmis le ${formatDate(base.date)}`}
        </p>
      )}
      {isNotNil(fichierId) && (
        <button
          type="button"
          onClick={() => onOpenFile(fichierId)}
          className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
        >
          <FilePdfIcon size={16} />
          {base.type === "visa" ? "Voir le visa" : "Voir le rapport"}
        </button>
      )}
    </div>
  );
}
